// src/pages/admin/FollowUpManager.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import {
  ArrowLeft,
  RefreshCw,
  CheckCircle,
  XCircle,
  Mail,
  Edit3,
  Pause,
  AlertCircle,
  Calendar,
  Filter,
} from 'lucide-react';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  try {
    // dateStr is 'YYYY-MM-DD' — parse as local date to avoid UTC midnight drift
    const [y, m, d] = dateStr.split('-').map(Number);
    const target = new Date(y, m - 1, d);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffMs = target - today;
    return Math.round(diffMs / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
}

function FollowUpManager() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const [sortBy, setSortBy] = useState('expectedReturnDate'); // 'expectedReturnDate' | 'createdAt' | 'childName'
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'
  const [showResolved, setShowResolved] = useState(false);

  const [editModal, setEditModal] = useState({ open: false, entry: null, notes: '', expectedReturnDate: '', submitting: false });

  // ── Fetch ─────────────────────────────────────────────────────────────────

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const snap = await getDocs(collection(db, 'users'));
      const users = snap.docs.map(d => ({ uid: d.id, ...d.data() }));
      setAllUsers(users);
    } catch (err) {
      console.error('FollowUp fetch error:', err);
      setError('Failed to load follow-ups.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // ── Flatten to entries ────────────────────────────────────────────────────

  const entries = useMemo(() => {
    const out = [];
    allUsers.forEach(u => {
      const children = Array.isArray(u.children) ? u.children : [];
      children.forEach(child => {
        const followUps = Array.isArray(child.followUps) ? child.followUps : [];
        followUps.forEach(fu => {
          const isResolved = !!fu.resolvedAt;
          if (!showResolved && isResolved) return;
          out.push({
            userId: u.uid,
            userEmail: u.email || '',
            userName: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || u.uid,
            childId: child.id,
            childName: child.name || 'Unknown',
            courseStatus: child.course?.status || '—',
            followUp: fu,
          });
        });
      });
    });

    const sorted = [...out].sort((a, b) => {
      let av, bv;
      if (sortBy === 'expectedReturnDate') {
        av = a.followUp.expectedReturnDate || '';
        bv = b.followUp.expectedReturnDate || '';
      } else if (sortBy === 'createdAt') {
        av = a.followUp.createdAt || '';
        bv = b.followUp.createdAt || '';
      } else {
        av = (a.childName || '').toLowerCase();
        bv = (b.childName || '').toLowerCase();
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [allUsers, showResolved, sortBy, sortDir]);

  const unresolvedCount = useMemo(() => {
    let n = 0;
    allUsers.forEach(u => {
      (u.children || []).forEach(c => {
        (c.followUps || []).forEach(f => {
          if (!f.resolvedAt) n++;
        });
      });
    });
    return n;
  }, [allUsers]);

  // ── Update a single followUp entry ────────────────────────────────────────

  const updateFollowUp = async (userId, childId, followUpId, patch) => {
    const userData = allUsers.find(u => u.uid === userId);
    if (!userData || !Array.isArray(userData.children)) {
      throw new Error('User or children not found.');
    }
    const updatedChildren = userData.children.map(child => {
      if (child.id !== childId) return child;
      const existing = Array.isArray(child.followUps) ? child.followUps : [];
      const updatedFollowUps = existing.map(f => f.id === followUpId ? { ...f, ...patch } : f);
      return { ...child, followUps: updatedFollowUps };
    });
    await updateDoc(doc(db, 'users', userId), { children: updatedChildren });
  };

  // ── Actions ───────────────────────────────────────────────────────────────

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3500);
  };

  const handleMarkContacted = async (entry) => {
    const key = `${entry.childId}__${entry.followUp.id}__contacted`;
    setActionLoading(key);
    try {
      await updateFollowUp(entry.userId, entry.childId, entry.followUp.id, {
        contactedAt: new Date().toISOString(),
      });
      showSuccess(`Marked ${entry.childName} as contacted.`);
      await fetchAll();
    } catch (err) {
      console.error(err);
      setError(`Failed to update ${entry.childName}.`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkReturned = async (entry) => {
    if (!window.confirm(`Mark ${entry.childName} as confirmed returning?\n\nThis removes them from the follow-up list. Their course remains expired until you process renewal payment through the Renewals page.\n\nIf they've already paid via Stripe, the system will mark this automatically — no action needed here.`)) return;
    const key = `${entry.childId}__${entry.followUp.id}__returned`;
    setActionLoading(key);
    try {
      await updateFollowUp(entry.userId, entry.childId, entry.followUp.id, {
        resolvedAt: new Date().toISOString(),
        resolvedReason: 'returned',
        resolvedBy: user?.uid || 'unknown_admin',
      });
      showSuccess(`Marked ${entry.childName} as returned.`);
      await fetchAll();
    } catch (err) {
      console.error(err);
      setError(`Failed to update ${entry.childName}.`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkNotReturning = async (entry) => {
    if (!window.confirm(`Mark ${entry.childName} as NOT returning?\n\nThis removes them from the active follow-up list. The entry is preserved in history.`)) return;
    const key = `${entry.childId}__${entry.followUp.id}__notreturn`;
    setActionLoading(key);
    try {
      await updateFollowUp(entry.userId, entry.childId, entry.followUp.id, {
        resolvedAt: new Date().toISOString(),
        resolvedReason: 'not_returning',
        resolvedBy: user?.uid || 'unknown_admin',
      });
      showSuccess(`Marked ${entry.childName} as not returning.`);
      await fetchAll();
    } catch (err) {
      console.error(err);
      setError(`Failed to update ${entry.childName}.`);
    } finally {
      setActionLoading(null);
    }
  };

  const openEditModal = (entry) => {
    setEditModal({
      open: true,
      entry,
      notes: entry.followUp.notes || '',
      expectedReturnDate: entry.followUp.expectedReturnDate || '',
      submitting: false,
    });
  };

  const closeEditModal = () => {
    setEditModal({ open: false, entry: null, notes: '', expectedReturnDate: '', submitting: false });
  };

  const handleSaveEdit = async () => {
    const { entry, notes, expectedReturnDate } = editModal;
    if (!entry) return;

    if (!expectedReturnDate) {
      setError('Expected return date is required.');
      return;
    }

    setEditModal(prev => ({ ...prev, submitting: true }));
    try {
      const patch = {
        notes: notes.trim(),
        expectedReturnDate,
        lastEditedAt: new Date().toISOString(),
        lastEditedBy: user?.uid || 'unknown_admin',
      };
      await updateFollowUp(entry.userId, entry.childId, entry.followUp.id, patch);
      showSuccess(`Follow-up updated for ${entry.childName}.`);
      closeEditModal();
      await fetchAll();
    } catch (err) {
      console.error(err);
      setError(`Failed to update follow-up.`);
      setEditModal(prev => ({ ...prev, submitting: false }));
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading follow-ups...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <Pause size={22} className="text-amber-600" />
                Follow-Up List
              </h1>
              <p className="text-sm text-gray-500">Paused students tracked for return</p>
            </div>
          </div>
          <button
            onClick={fetchAll}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
        </div>

        {/* Banners */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-2">
            <AlertCircle size={18} className="mt-0.5 shrink-0" />
            <div className="flex-1">{error}</div>
            <button onClick={() => setError(null)} className="text-red-700 hover:text-red-900">
              <XCircle size={18} />
            </button>
          </div>
        )}
        {successMsg && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-start gap-2">
            <CheckCircle size={18} className="mt-0.5 shrink-0" />
            <div className="flex-1">{successMsg}</div>
          </div>
        )}

        {/* Summary + Controls */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="text-sm">
              <span className="text-gray-500">Unresolved: </span>
              <span className="font-bold text-amber-600">{unresolvedCount}</span>
            </div>
            <div className="text-sm">
              <span className="text-gray-500">Showing: </span>
              <span className="font-bold text-gray-800">{entries.length}</span>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={showResolved}
                onChange={(e) => setShowResolved(e.target.checked)}
                className="rounded"
              />
              Show resolved
            </label>
            <div className="flex items-center gap-1">
              <Filter size={14} className="text-gray-400" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-sm border border-gray-200 rounded px-2 py-1"
              >
                <option value="expectedReturnDate">Sort: Expected return</option>
                <option value="createdAt">Sort: Pause date</option>
                <option value="childName">Sort: Child name</option>
              </select>
              <button
                onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                className="text-sm border border-gray-200 rounded px-2 py-1 hover:bg-gray-50"
                title="Toggle direction"
              >
                {sortDir === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        {entries.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-500">
            <Pause size={36} className="mx-auto mb-3 text-gray-300" />
            <p>No follow-ups to show.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="px-4 py-3 text-left">Child</th>
                    <th className="px-4 py-3 text-left">Parent</th>
                    <th className="px-4 py-3 text-left">Paused</th>
                    <th className="px-4 py-3 text-left">Expected return</th>
                    <th className="px-4 py-3 text-left">Notes</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {entries.map(entry => {
                    const fu = entry.followUp;
                    const days = daysUntil(fu.expectedReturnDate);
                    const isOverdue = days !== null && days < 0 && !fu.resolvedAt;
                    const isSoon = days !== null && days >= 0 && days <= 7 && !fu.resolvedAt;

                    return (
                      <tr key={`${entry.childId}__${fu.id}`} className={isOverdue ? 'bg-red-50/40' : ''}>
                        <td className="px-4 py-3 font-medium text-gray-800">{entry.childName}</td>
                        <td className="px-4 py-3 text-gray-600">
                          <div>{entry.userName}</div>
                          {entry.userEmail && (
                            <div className="text-xs text-gray-400">{entry.userEmail}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(fu.createdAt)}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Calendar size={13} className="text-gray-400" />
                            <span className="text-gray-800">{formatDate(fu.expectedReturnDate)}</span>
                            {fu.lastEditedAt && (
                              <span
                                className="text-[10px] text-gray-400 italic"
                                title={`Last edited ${formatDate(fu.lastEditedAt)}`}
                              >
                                (edited)
                              </span>
                            )}
                          </div>
                          {!fu.resolvedAt && days !== null && (
                            <div className={`text-xs mt-0.5 ${isOverdue ? 'text-red-600 font-medium' : isSoon ? 'text-amber-600 font-medium' : 'text-gray-400'}`}>
                              {isOverdue ? `${Math.abs(days)}d overdue` : days === 0 ? 'today' : `${days}d away`}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600 max-w-xs">
                          <div className="truncate" title={fu.notes}>{fu.notes || '—'}</div>
                        </td>
                        <td className="px-4 py-3">
                          {fu.resolvedAt ? (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              fu.resolvedReason === 'returned'
                                ? (fu.resolvedBy === 'system_renewal_webhook' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700')
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {fu.resolvedReason === 'returned'
                                ? (fu.resolvedBy === 'system_renewal_webhook' ? '✓ Renewed' : '✓ Confirmed returning')
                                : '✗ Not returning'}
                            </span>
                          ) : fu.contactedAt ? (
                            <span
                              className="text-xs px-2 py-0.5 rounded-full font-medium bg-sky-100 text-sky-700"
                              title={`Contacted ${formatDate(fu.contactedAt)}`}
                            >
                              📨 Awaiting reply
                            </span>
                          ) : (
                            <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">
                              ⏸ Pending
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {fu.resolvedAt ? (
                            <div className="text-xs text-gray-400 text-right">
                              Resolved {formatDate(fu.resolvedAt)}
                            </div>
                          ) : (
                            <div className="flex items-center justify-end gap-1 flex-wrap">
                              <button
                                onClick={() => handleMarkContacted(entry)}
                                disabled={actionLoading?.includes(fu.id)}
                                title="Mark as contacted"
                                className="p-1.5 rounded hover:bg-blue-50 text-blue-600 disabled:opacity-50"
                              >
                                <Mail size={14} />
                              </button>
                              <button
                                onClick={() => openEditModal(entry)}
                                disabled={actionLoading?.includes(fu.id)}
                                title="Edit follow-up (date + notes)"
                                className="p-1.5 rounded hover:bg-gray-100 text-gray-600 disabled:opacity-50"
                              >
                                <Edit3 size={14} />
                              </button>
                              <button
                                onClick={() => handleMarkReturned(entry)}
                                disabled={actionLoading?.includes(fu.id)}
                                title="Confirmed returning (process payment separately)"
                                className="p-1.5 rounded hover:bg-green-50 text-green-600 disabled:opacity-50"
                              >
                                <CheckCircle size={14} />
                              </button>
                              <button
                                onClick={() => handleMarkNotReturning(entry)}
                                disabled={actionLoading?.includes(fu.id)}
                                title="Mark as not returning"
                                className="p-1.5 rounded hover:bg-red-50 text-red-600 disabled:opacity-50"
                              >
                                <XCircle size={14} />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <p className="text-xs text-gray-400 text-center pb-4">
          When a paused child renews through Stripe, their follow-up is auto-resolved as "returned".
          Resolved entries are preserved for analytics.
        </p>
      </div>

      {/* Edit Follow-Up Modal */}
      {editModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <Edit3 size={18} className="text-gray-600" />
                Edit follow-up — {editModal.entry?.childName}
              </h3>
              <button onClick={closeEditModal} disabled={editModal.submitting} className="text-gray-400 hover:text-gray-600 disabled:opacity-50">
                <XCircle size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expected return date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={editModal.expectedReturnDate}
                  onChange={(e) => setEditModal(prev => ({ ...prev, expectedReturnDate: e.target.value }))}
                  disabled={editModal.submitting}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes <span className="text-gray-400">(optional)</span>
                </label>
                <textarea
                  value={editModal.notes}
                  onChange={(e) => setEditModal(prev => ({ ...prev, notes: e.target.value }))}
                  disabled={editModal.submitting}
                  rows={4}
                  placeholder="e.g., Trip extended, family will be back early September"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                />
              </div>

              {editModal.entry?.followUp?.lastEditedAt && (
                <p className="text-xs text-gray-400">
                  Last edited {formatDate(editModal.entry.followUp.lastEditedAt)}
                </p>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={closeEditModal}
                disabled={editModal.submitting}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={editModal.submitting || !editModal.expectedReturnDate}
                className="flex-1 px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {editModal.submitting ? <><RefreshCw size={14} className="animate-spin" /> Saving...</> : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FollowUpManager;
